import { useEffect, useState } from 'react';
import { CheckCircle2, Loader2, MailWarning, XCircle } from 'lucide-react';
import { Link, useSearch } from 'wouter';

import { PublicShell } from '@/components/public-shell';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { apiRequest, ApiError, queryClient } from '@/lib/queryClient';

type VerificationStatus = 'loading' | 'success' | 'error' | 'missing';

export default function VerifyEmail() {
  const search = useSearch();
  const token = new URLSearchParams(search).get('token') ?? '';
  const [status, setStatus] = useState<VerificationStatus>(token ? 'loading' : 'missing');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      return;
    }

    let cancelled = false;

    async function verifyEmail() {
      try {
        // apiRequest retries the cold-start signal (503) so a transient DB wake
        // doesn't show a false "verification failed". The token isn't consumed on
        // a cold-start failure (the request never reached business logic).
        const response = await apiRequest(
          'GET',
          `/api/auth/verify-email?token=${encodeURIComponent(token)}`
        );
        const data = await response.json().catch(() => ({}));

        if (cancelled) {
          return;
        }

        setStatus('success');
        setMessage(data.message || 'Email verified successfully.');
        void queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      } catch (error) {
        if (cancelled) {
          return;
        }

        let msg = 'Unable to verify this email link.';
        if (error instanceof ApiError) {
          try {
            msg = JSON.parse(error.body)?.error || msg;
          } catch {
            /* non-JSON error body — keep the default message */
          }
        }
        setStatus('error');
        setMessage(msg);
      }
    }

    void verifyEmail();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const content = {
    loading: {
      icon: <Loader2 className="h-8 w-8 animate-spin" />,
      title: 'Verifying your email',
      description: 'Give us a moment while we confirm this address.',
    },
    success: {
      icon: <CheckCircle2 className="h-8 w-8" />,
      title: 'Email verified',
      description: message || 'Your Kalovate account email is now verified.',
    },
    error: {
      icon: <XCircle className="h-8 w-8" />,
      title: 'Verification link did not work',
      description:
        message || 'This link may be expired or already used. Request a new verification email.',
    },
    missing: {
      icon: <MailWarning className="h-8 w-8" />,
      title: 'Verification link missing',
      description: 'Open the link from your verification email or request a fresh link.',
    },
  }[status];

  return (
    <PublicShell routeKey="account">
      <section className="page-shell-narrow py-12">
        <Card className="rounded-[2rem] border-border/70 shadow-sm">
          <CardContent className="space-y-6 p-8 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
              {content.icon}
            </div>
            <div className="space-y-3">
              <p className="editorial-kicker">Account security</p>
              <h1 className="text-4xl font-semibold">{content.title}</h1>
              <p className="section-copy">{content.description}</p>
            </div>

            <div className="flex flex-col justify-center gap-3 sm:flex-row">
              <Link href="/account">
                <Button className="rounded-full px-5">Go to account</Button>
              </Link>
              <Link href="/">
                <Button variant="outline" className="rounded-full px-5">
                  Browse Kalovate
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </section>
    </PublicShell>
  );
}
